/**
 * AP-48 7. — **Bautagebuch eines Projekts** (Baustellendokumente → Bautagebuch): Projektwahl, Monatskalender
 * mit den Bautagesberichten je Tag (Zustand, gegengezeichnet, Einwand, Stillstand, Personen und Stunden), Tage
 * mit Buchungen ohne Bericht als „Bericht fehlt" (TB-12, nur mit Projektzeit). Von hier: freigegebene Berichte
 * markieren und zur Gegenzeichnung senden (RN-6), Bautagebuch eines Zeitraums als PDF — auch per Mail (TB-13).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronLeft, ChevronRight, ListChecks, MessageSquareWarning, PenLine, Send } from 'lucide-react'
import { SITE_DOC_COUNTERSIGN_MAX, erfuelltRecht, type SiteDocStatus, type TagebuchKalender, type TagebuchKalenderTag } from '@symphenia/shared-types'
import ModuleLockedPage from '@/components/ModuleLockedPage'
import { ExportButton } from '@/components/ui/ExportButton'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate, fmtMonthYear, parseLocalDate, toIsoDate, userLocale } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { cn } from '@/lib/utils'
import { tagebuchBuchRequest, tagebuchKalender } from '../api/site-docs.api'
import { GegenzeichnungSendenDialog, type GegenzeichnungDokument } from '../components/GegenzeichnungSenden'
import { ProjektWahl, type ProjektWahlWert } from '../components/ProjektWahl'
import { StatusPill } from '../components/StatusPill'

type Bericht = TagebuchKalenderTag['reports'][number]

const monatVon = (d: Date) => toIsoDate(d).slice(0, 7)
const verschiebe = (monat: string, um: number) => {
  const [j, m] = monat.split('-').map(Number)
  return monatVon(new Date(j, m - 1 + um, 1))
}
const letzterTag = (monat: string) => {
  const [j, m] = monat.split('-').map(Number)
  return toIsoDate(new Date(j, m, 0))
}

function BerichtKarte({ r, markiert, waehlbar, onMarke }: { r: Bericht; markiert: boolean; waehlbar: boolean; onMarke: (an: boolean) => void }) {
  const { t } = useTranslation()
  return (
    <div className={cn('space-y-0.5 rounded border bg-background px-1.5 py-1 text-xs', markiert && 'border-primary ring-1 ring-primary')}>
      <div className="flex items-center gap-1">
        {r.status === 'FINAL' && (
          <input type="checkbox" aria-label={t('siteDocs.diary.mark', { nummer: r.number ?? '' })} checked={markiert} disabled={!waehlbar && !markiert} onChange={(e) => onMarke(e.target.checked)} />
        )}
        <Link to={`/site-docs/${r.id}`} className="truncate font-medium text-primary hover:underline" title={r.title}>
          {r.number ?? t('siteDocs.status.DRAFT')}
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <StatusPill status={r.status as SiteDocStatus} />
        {r.countersigned && (
          <span className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-300" title={t('siteDocs.diary.countersigned')}>
            <PenLine className="h-3 w-3" />
          </span>
        )}
        {r.objection && (
          <span className="inline-flex items-center gap-0.5 text-amber-700 dark:text-amber-300" title={t('siteDocs.diary.objection')}>
            <MessageSquareWarning className="h-3 w-3" /> {t('siteDocs.diary.objection')}
          </span>
        )}
      </div>
      {r.standstill ? (
        <p className="font-medium text-amber-800 dark:text-amber-200">{t('siteDocs.diary.standstillShort')}</p>
      ) : (
        r.persons > 0 && <p className="text-muted-foreground">{t('siteDocs.diary.personsHours', { personen: r.persons, stunden: String(r.hours).replace('.', ',') })}</p>
      )}
      <p className="truncate text-muted-foreground">{r.createdByName}</p>
    </div>
  )
}

export default function BautagebuchPage() {
  const { t } = useTranslation()
  const { user, hasModule } = useAuthStore()
  const darfSenden = erfuelltRecht('sitedocs.document.send', user?.permissions)
  const darfExport = erfuelltRecht('sitedocs.export.run', user?.permissions)
  const [projekt, setProjekt] = useState<ProjektWahlWert | null>(null)
  const [monat, setMonat] = useState(() => monatVon(new Date()))
  const [kalender, setKalender] = useState<TagebuchKalender | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [markiert, setMarkiert] = useState<Map<string, GegenzeichnungDokument>>(new Map())
  const [senden, setSenden] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [von, setVon] = useState(`${monat}-01`)
  const [bis, setBis] = useState(letzterTag(monat))

  const lade = useCallback(async () => {
    if (!projekt) return
    setLaedt(true)
    setFehler(null)
    try {
      setKalender(await tagebuchKalender({ ...projekt.bezug, month: monat }))
    } catch (e) {
      setFehler(fehlertext(e, t))
      setKalender(null)
    } finally {
      setLaedt(false)
    }
  }, [projekt, monat, t])

  useEffect(() => {
    void lade()
  }, [lade])

  useEffect(() => {
    setVon(`${monat}-01`)
    setBis(letzterTag(monat))
  }, [monat])

  // Wochen ab Montag; vor dem Ersten leere Zellen.
  const zellen = useMemo(() => {
    if (!kalender) return []
    const erster = parseLocalDate(kalender.days[0]?.date ?? `${monat}-01`)
    const leer = (erster.getDay() + 6) % 7
    return [...Array<null>(leer).fill(null), ...kalender.days]
  }, [kalender, monat])
  const wochentage = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(userLocale(), { weekday: 'short' })), [])

  if (!hasModule('SITE_DOCS.DIARY')) return <ModuleLockedPage moduleKey="SITE_DOCS.DIARY" />

  const voll = markiert.size >= SITE_DOC_COUNTERSIGN_MAX
  const markiere = (r: Bericht, tag: string, an: boolean) =>
    setMarkiert((m) => {
      const n = new Map(m)
      if (an) n.set(r.id, { id: r.id, number: r.number, documentDate: tag, title: r.title })
      else n.delete(r.id)
      return n
    })
  const freigegebene = kalender?.days.flatMap((d) => d.reports.filter((r) => r.status === 'FINAL').map((r) => ({ r, tag: d.date }))) ?? []
  const fehlende = kalender?.bookingsAvailable ? kalender.days.filter((d) => d.missing).length : 0
  const titel = t('mailExport.titles.EXPORT_SITE_DIARY_BOOK')

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('siteDocs.diary.bookTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('siteDocs.diary.bookSubtitle')}</p>
        </div>
        <Link to="/site-docs/diary/hindrances" className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted">
          <AlertTriangle className="h-4 w-4" /> {t('siteDocs.diary.hindranceList')}
        </Link>
      </div>

      <ProjektWahl
        wert={projekt}
        onWahl={(w) => {
          setProjekt(w)
          setMarkiert(new Map())
          setMeldung(null)
        }}
      />

      {projekt && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted" aria-label={t('siteDocs.diary.prevMonth')} onClick={() => setMonat((m) => verschiebe(m, -1))}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <h2 className="min-w-40 text-center text-lg font-semibold">{fmtMonthYear(`${monat}-01`)}</h2>
              <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted" aria-label={t('siteDocs.diary.nextMonth')} onClick={() => setMonat((m) => verschiebe(m, 1))}>
                <ChevronRight className="h-4 w-4" />
              </button>
              {fehlende > 0 && <span className="text-sm text-amber-700 dark:text-amber-300">{t('siteDocs.diary.missingCount', { count: fehlende })}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {darfSenden && freigegebene.length > 0 && (
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted"
                  onClick={() => {
                    const n = new Map(markiert)
                    for (const { r, tag } of freigegebene) if (n.size < SITE_DOC_COUNTERSIGN_MAX) n.set(r.id, { id: r.id, number: r.number, documentDate: tag, title: r.title })
                    setMarkiert(n)
                  }}
                >
                  <ListChecks className="h-4 w-4" /> {t('siteDocs.diary.markAllFinal')}
                </button>
              )}
              {darfSenden && (
                <button type="button" disabled={markiert.size === 0} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm text-primary-foreground disabled:opacity-50" onClick={() => setSenden(true)}>
                  <Send className="h-4 w-4" /> {t('siteDocs.diary.sendMarked', { count: markiert.size })}
                </button>
              )}
              {darfExport && (
                <ExportButton
                  label={t('siteDocs.diary.bookPdf')}
                  defaultFilename="bautagebuch"
                  options={[{ format: 'pdf', labelKey: 'export.pdf' }]}
                  showOrientation={false}
                  request={() => tagebuchBuchRequest({ ...projekt.bezug, from: von, to: bis })}
                  extra={
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs text-muted-foreground">
                        {t('siteDocs.filter.from')}
                        <input type="date" className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground" value={von} onChange={(e) => setVon(e.target.value)} />
                      </label>
                      <label className="text-xs text-muted-foreground">
                        {t('siteDocs.filter.to')}
                        <input type="date" className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground" value={bis} onChange={(e) => setBis(e.target.value)} />
                      </label>
                    </div>
                  }
                  mail={{ source: 'EXPORT_SITE_DIARY_BOOK', title: titel, subject: `${titel} ${projekt.label} ${fmtDate(von)}–${fmtDate(bis)}` }}
                />
              )}
            </div>
          </div>
          {meldung && <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100">{meldung}</p>}
          {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
          {laedt && !kalender && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {voll && <p className="text-sm text-muted-foreground">{t('siteDocs.countersign.tooMany', { max: SITE_DOC_COUNTERSIGN_MAX })}</p>}

          {kalender && (
            <div className="overflow-x-auto">
              <div className="grid min-w-[56rem] grid-cols-7 gap-1" role="grid" aria-label={fmtMonthYear(`${monat}-01`)}>
                {wochentage.map((w) => (
                  <div key={w} className="px-1 text-xs font-semibold uppercase text-muted-foreground">
                    {w}
                  </div>
                ))}
                {zellen.map((d, i) =>
                  d === null ? (
                    <div key={`leer-${i}`} />
                  ) : (
                    <div
                      key={d.date}
                      role="gridcell"
                      data-tag={d.date}
                      className={cn('min-h-24 space-y-1 rounded-md border p-1', d.missing && kalender.bookingsAvailable ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'bg-muted/20')}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold">{Number(d.date.slice(8, 10))}</span>
                        {kalender.bookingsAvailable && d.bookings > 0 && <span className="text-muted-foreground">{t('siteDocs.diary.bookings', { count: d.bookings })}</span>}
                      </div>
                      {d.missing && kalender.bookingsAvailable && <p className="text-xs font-medium text-amber-800 dark:text-amber-200">{t('siteDocs.diary.reportMissing')}</p>}
                      {d.reports.map((r) => (
                        <BerichtKarte key={r.id} r={r} markiert={markiert.has(r.id)} waehlbar={darfSenden && !voll} onMarke={(an) => markiere(r, d.date, an)} />
                      ))}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}

      {senden && projekt && (
        <GegenzeichnungSendenDialog
          dokumente={[...markiert.values()].sort((a, b) => a.documentDate.localeCompare(b.documentDate))}
          kontakte={projekt.kontakte}
          onClose={() => setSenden(false)}
          onGesendet={(a) => {
            setSenden(false)
            setMarkiert(new Map())
            setMeldung(t('siteDocs.countersign.sentTo', { email: a.email }))
          }}
        />
      )}
    </div>
  )
}
